/**
 * Plant + memory definitions for Heirloom.
 * Each plant exists in the garden; tending it together unlocks its memory.
 *
 * Positions are NORMALIZED to the VIEWPORT (0..1 range). The background
 * painting is cropped with "cover" scaling, so painting-coords don't work
 * on narrow mobile screens — viewport-coords stay visible on any aspect ratio.
 *
 * Layout (designed for portrait mobile):
 *   rose:      center, just below middle          (pedestal area)
 *   fig:       left third, above middle           (left ivy area)
 *   blueberry: right third, above middle          (right ivy area)
 *   bulb:      lower-left, near bottom            (cobblestone / ferns)
 */
export const PLANTS = [
  {
    id: 'rose_first',
    name: 'a single rose',
    nx: 0.50, ny: 0.58,
    color: 0xff6b9d,
    memoryId: 'wedding_dance',
  },
  {
    id: 'fig_tree',
    name: 'a young fig tree',
    nx: 0.22, ny: 0.40,
    color: 0xc89078,
    memoryId: 'first_son',
  },
  {
    id: 'blueberry_bush',
    name: 'a blueberry bush',
    nx: 0.78, ny: 0.40,
    color: 0x7090e0,
    memoryId: 'two_children',
  },
  {
    id: 'buried_bulb',
    name: 'something buried',
    nx: 0.32, ny: 0.76,
    color: 0xf0c060,
    memoryId: 'unsent_letter',
  },
];

export const MEMORIES = {
  wedding_dance: {
    title: 'Summer, 1962',
    music: 'wedding_dance_memory',
    backdrop: 'memory_wedding',
    backdropColor: 0xff80ab,
    beats: [
      { delay: 1200, text: 'She wore the white dress her mother had worn.', voice: 'grandma_intro_1' },
      { delay: 4500, text: 'The roses in her hair were from this same garden.', voice: 'grandma_intro_2' },
      { delay: 4500, text: "He couldn't stop looking at her.", voice: 'grandma_intro_3' },
      { delay: 4500, text: 'They danced under the lanterns until the music stopped.', voice: 'grandma_intro_4' },
      { delay: 4500, text: 'And then they kept dancing.', voice: 'grandma_intro_5' },
      { delay: 5000, text: 'She would carry that night with her for the rest of her life.', voice: 'grandma_intro_6' },
    ],
  },
  first_son: {
    title: 'Spring, 1965',
    music: 'fig_tree_memory',
    backdrop: 'memory_fig',
    backdropColor: 0xa0c878,
    beats: [
      { delay: 1200, text: 'The fig tree was a gift from her mother.', voice: 'grandma_fig_1' },
      { delay: 4500, text: '"You plant it on the day you bring a child home," she said.', voice: 'grandma_fig_2' },
      { delay: 5000, text: 'So she planted it the morning she carried her son through the door.', voice: 'grandma_fig_3' },
      { delay: 5000, text: 'He was so small. So new.', voice: 'grandma_fig_4' },
      { delay: 4500, text: 'The tree grew faster than he did.', voice: 'grandma_fig_5' },
      { delay: 5000, text: 'But she watched them both with the same quiet wonder.', voice: 'grandma_fig_6' },
    ],
  },
  two_children: {
    title: 'August, 1978',
    music: 'blueberry_memory',
    backdrop: 'memory_blueberry',
    backdropColor: 0x6080d0,
    beats: [
      { delay: 1200, text: 'Two children used to come here at the end of summer.', voice: 'grandma_blue_1' },
      { delay: 4500, text: 'A boy and his little sister.', voice: 'grandma_blue_2' },
      { delay: 4000, text: 'They would fill their pockets with blueberries...', voice: 'grandma_blue_3' },
      { delay: 4500, text: '...until their lips and fingers were stained purple.', voice: 'grandma_blue_4' },
      { delay: 5000, text: 'She watched them every August. They never saw her.', voice: 'grandma_blue_5' },
      { delay: 5500, text: 'She wonders, sometimes, what they grew up to be.', voice: 'grandma_blue_6' },
    ],
  },
  unsent_letter: {
    title: 'Winter, 2003',
    music: 'buried_bulb_memory',
    backdrop: 'memory_letter',
    backdropColor: 0xc8a868,
    beats: [
      { delay: 1200, text: 'There was a letter she wrote and never sent.', voice: 'grandma_letter_1' },
      { delay: 4500, text: 'She buried it in the garden, in a tin, under a tulip bulb.', voice: 'grandma_letter_2' },
      { delay: 5000, text: '"If anyone ever finds this," she had written,', voice: 'grandma_letter_3' },
      { delay: 4500, text: '"please know I am sorry I did not say it sooner."', voice: 'grandma_letter_4' },
      { delay: 5000, text: '"You were the best thing I ever made."', voice: 'grandma_letter_5' },
      { delay: 5500, text: 'She did not say who she was writing to.', voice: 'grandma_letter_6' },
      { delay: 5000, text: 'Maybe she was writing to whoever found it.', voice: 'grandma_letter_7' },
    ],
  },
};
